import i18n from '@/utils/i18n';
import React, { useEffect, useMemo } from 'react';
import { ACTION_SHEET_MAX_HEIGHT, SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { ActivityIndicator } from '@/components/activity-indicator';
import {
  AllowedShortcuts,
  KeyboardScope,
} from '@/platform/shortcuts/operator-shortcuts';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { EssentialReminderList } from './essential-reminder-list';
import { gql } from '@apollo/client';
import { ItemDetailContextProvider } from '@/contexts/item-detail-context';
import {
  ItemsContext_VariantsPackFieldsFragment,
  OrderSummary_MissingEssentialItemsQuery,
  OrderSummary_MissingEssentialItemsQueryVariables,
} from '@/types';
import { StyleSheet, Text, useWindowDimensions } from 'react-native';
import { UpdateQuantityFn } from '@/contexts/new-order-actions-context-types';
import { useAdaptAndGroupVariantPacks } from '@/hooks/use-adapt-and-group-item-list-options';
import { useItemQueryVariables } from '@/hooks/use-item-query-variables';
import { useKeyboardShortcuts } from '@platform/shortcuts/hooks/use-keyboard-shortcuts';
import { useLayoutContext } from '@/contexts/layout-context';
import { useUserContext } from '@/contexts/user-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';
import { VARIANTS_PACK_FRAGMENT } from '@/contexts/items-context-queries';

interface Props {
  essentialItemUUIDs: string[];
  footer: React.ReactNode;
  onClose: () => void;
  onError: () => void;
  onSubmitEssentialItems: () => void;
  updateQuantity: UpdateQuantityFn;
}

export const GET_MISSING_ESSENTIAL_ITEMS = gql`
  query OrderSummary_MissingEssentialItems(
    $locale: String!
    $restaurantUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $supplierUUID: uuid!
    $essentialItemUUIDs: [uuid!]!
  ) {
    visible_variant_packs(
      where: {
        variant_pack_uuid: { _in: $essentialItemUUIDs }
        restaurant_uuid: { _eq: $restaurantUUID }
        supplier_uuid: { _eq: $supplierUUID }
      }
      order_by: { variant_pack: { item: { display_name: asc } } }
    ) {
      variant_pack {
        ...ItemsContext_VariantsPackFields
      }
    }
  }

  ${VARIANTS_PACK_FRAGMENT}
`;

export const EssentialItemReminderSheet: React.FC<Props> = ({
  essentialItemUUIDs,
  footer,
  onClose,
  onError,
  onSubmitEssentialItems,
  updateQuantity,
}) => {
  const itemQueryVariables = useItemQueryVariables();
  const { height } = useWindowDimensions();
  const { isSmallLayout } = useLayoutContext();
  const { selectedRestaurantUUID, selectedRestaurantRelationship } =
    useUserContext();

  useKeyboardShortcuts(
    AllowedShortcuts.PlaceOrder,
    onSubmitEssentialItems,
    [],
    {},
    true,
    KeyboardScope.ACTION_SHEET,
  );

  const { data, error, loading } = useWrappedQuery<
    OrderSummary_MissingEssentialItemsQuery,
    OrderSummary_MissingEssentialItemsQueryVariables
  >(GET_MISSING_ESSENTIAL_ITEMS, {
    variables: {
      ...itemQueryVariables,
      essentialItemUUIDs,
      restaurantUUID: selectedRestaurantUUID,
      supplierUUID: selectedRestaurantRelationship.supplierUUID,
    },
  });

  useEffect(() => {
    if (error) {
      onError();
    }
  }, [error, onError]);

  const adaptAndGroupVariantPacks = useAdaptAndGroupVariantPacks();

  const listData = useMemo(() => {
    const vvps = (data?.visible_variant_packs || []).reduce<
      ItemsContext_VariantsPackFieldsFragment[]
    >((acc, vvp) => {
      if (vvp?.variant_pack) {
        acc.push(vvp.variant_pack);
      }
      return acc;
    }, []);
    return adaptAndGroupVariantPacks(vvps);
  }, [data, adaptAndGroupVariantPacks]);

  const maxHeight = Math.floor(height * 0.85);

  return (
    <BaseActionSheet
      contentStyle={[
        styles.base,
        isSmallLayout ? styles.baseSmall : null,
        { maxHeight },
      ]}
      isFocusableForKeyboardShortcuts
      isVisible
      modalStyle={styles.modalStyle}
      onClose={onClose}
      title={i18n.t('forget_something')}
    >
      <ItemDetailContextProvider>
        {loading ? (
          <ActivityIndicator />
        ) : (
          <>
            {isSmallLayout ? (
              <Text style={[TYPOGRAPHY.h2, styles.title]}>
                {i18n.t('forget_something')}
              </Text>
            ) : null}
            <EssentialReminderList
              listData={listData}
              updateQuantity={updateQuantity}
            />
            {footer}
          </>
        )}
      </ItemDetailContextProvider>
    </BaseActionSheet>
  );
};

const styles = StyleSheet.create({
  base: {
    flex: 1,
    paddingHorizontal: 0,
  },
  baseSmall: {
    paddingTop: SCREEN_PADDING,
  },
  modalStyle: {
    flex: 1,
    maxHeight: ACTION_SHEET_MAX_HEIGHT,
  },
  title: {
    marginLeft: 16,
    paddingBottom: 16,
  },
});
